import { Capacitor, registerPlugin } from "@capacitor/core";
import { GenericOAuth2 } from "@capacitor-community/generic-oauth2";

import {
  OAUTH_GOOGLE_ACCESS_TOKEN_ENDPOINT,
  OAUTH_GOOGLE_AUTHORIZATION_BASE_URL,
  OAUTH_GOOGLE_IOS_CLIENT_ID,
  OAUTH_GOOGLE_IOS_REDIRECT_URI,
  OAUTH_GOOGLE_WEB_CLIENT_ID,
  OAUTH_GOOGLE_WEB_REDIRECT_URL,
} from "./env";
import { SessionDetails } from "./models/survey";
import { FetchStatus, postApi } from "./util";

type GoogleOauthPlugin = {
  login(options: { webClientId: string }): Promise<{ token?: string; fail?: string }>;

  logout(): Promise<void>;
};

type AuthorizationUrlResponse = {
  access_token: undefined;
  authorization_response: {
    authuser: string;
    code: string;
    prompt: string;
    scope: string;
    state: string;
  };
};

type AccessUrlResponse = {
  access_token_response: {
    access_token: string;
    expires_in: number;
    id_token: string;
    refresh_token: string;
    scope: string;
    token_type: "Bearer";
  };
};

class GoogleOauth {
  public static readonly AndroidPluginInst = registerPlugin<GoogleOauthPlugin>("GoogleOauthPlugin");

  public static async doGoogleOauthAndroid(complete: (res: SessionDetails) => void, error: (err: FetchStatus) => void) {
    const { token, fail } = await GoogleOauth.AndroidPluginInst.login({
      webClientId: OAUTH_GOOGLE_WEB_CLIENT_ID,
    });

    if (fail != null) {
      console.error("Failed to get Google Oauth JWT");
      error({ code: 400 });
      return;
    }

    void postApi<SessionDetails>(
      "/session/oauth/google_android_authenticate_jwt",
      {
        token: token,
      },
      complete,
      error
    );
  }

  public static async doGoogleOauthIos(complete: (res: SessionDetails) => void, error: (err: FetchStatus) => void) {
    const accessResponse: AccessUrlResponse | undefined = (await GenericOAuth2.authenticate({
      scope: "email openid", // Could also do "profile", but none of that data is needed
      logsEnabled: true,

      ios: {
        authorizationBaseUrl: OAUTH_GOOGLE_AUTHORIZATION_BASE_URL,
        accessTokenEndpoint: OAUTH_GOOGLE_ACCESS_TOKEN_ENDPOINT,

        pkceEnabled: true,
        appId: OAUTH_GOOGLE_IOS_CLIENT_ID,
        responseType: "code", // if you configured a ios app in google dev console the value must be "code"
        redirectUrl: OAUTH_GOOGLE_IOS_REDIRECT_URI, // Bundle ID from google dev console
      },
    }).catch((_err: unknown) => {})) as AccessUrlResponse | undefined;

    console.log("Google OAuth accepted", accessResponse);

    if (accessResponse === undefined) {
      error({ code: 400 });
      return;
    }

    void postApi<SessionDetails>(
      "/session/oauth/google_ios_authenticate_token",
      {
        token: accessResponse.access_token_response.id_token,
      },
      complete,
      error
    );
  }

  public static async doGoogleOauthWeb(complete: (res: SessionDetails) => void, error: (err: FetchStatus) => void) {
    const authorizationResponse: AuthorizationUrlResponse | undefined = (await GenericOAuth2.authenticate({
      scope: "email openid", // Could also do "profile", but none of that data is needed

      logsEnabled: true,

      web: {
        appId: OAUTH_GOOGLE_WEB_CLIENT_ID,
        responseType: "code",
        authorizationBaseUrl: OAUTH_GOOGLE_AUTHORIZATION_BASE_URL,
        redirectUrl: OAUTH_GOOGLE_WEB_REDIRECT_URL,

        pkceEnabled: false,

        // Uncomment to present OAuth window as a popup, not a new tab
        windowOptions: "height=600,left=0,top=0",
      },
    }).catch((_err: unknown) => {})) as AuthorizationUrlResponse | undefined;

    // todo: This gets stuck on opening the redirect window sometimes? in the new window, you can click the
    // button again and it'll work
    // A potential solution is to make the redirect URI a page that is blank HTML, no Angular

    console.log("Google OAuth accepted", authorizationResponse);

    if (authorizationResponse === undefined) {
      error({ code: 400 });
      return;
    }

    void postApi<SessionDetails>(
      "/session/oauth/google_web_authenticate_token",
      {
        code: authorizationResponse.authorization_response.code,
      },
      complete,
      error
    );
  }

  static async doGoogleOauth(complete: (res: SessionDetails) => void, error: (err: FetchStatus) => void) {
    if (Capacitor.getPlatform() == "android") {
      await this.doGoogleOauthAndroid(complete, error);
    } else if (Capacitor.getPlatform() == "ios") {
      await this.doGoogleOauthIos(complete, error);
    } else {
      await this.doGoogleOauthWeb(complete, error);
    }
  }

  static async logout() {
    if (Capacitor.getPlatform() == "android") {
      await GoogleOauth.AndroidPluginInst.logout();
    }
  }
}

export { GoogleOauth };
